<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { type Shift, type ShiftWithoutFinancialInformation } from '@/api/shifts';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';

const { t } = useI18n();

const props = defineProps<{
  shift: Shift | ShiftWithoutFinancialInformation;
}>();

const showDialog = defineModel<boolean>();

function closeDialog() {
  showDialog.value = false;
}
</script>
<template>
  <v-dialog
    v-model="showDialog"
    max-width="900"
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="me-2">
          mdi-timeline-clock-outline
        </v-icon>
        <h2 class="text-h6">
          {{ t('shiftReportTextsTimeline.title') }}
        </h2>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="closeDialog"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="fixed-height">
        <div
          v-if="props.shift.reportTexts.length === 0"
          class="text-center text-grey pa-8"
        >
          <v-icon
            size="48"
            class="mb-4"
          >
            mdi-timeline-clock-outline
          </v-icon>
          <div>{{ t('shiftReportTextsTimeline.noReportTexts') }}</div>
        </div>
        <div
          v-else
          class="timeline-container"
        >
          <div
            v-for="(report, index) in props.shift.reportTexts"
            :key="report.id"
            class="mb-4 timeline-item"
          >
            <div class="d-flex align-start">
              <div class="mt-1 timeline-dot me-3">
                <v-icon
                  size="12"
                  color="primary"
                >
                  mdi-circle
                </v-icon>
              </div>
              <div class="timeline-content flex-grow-1">
                <div class="timeline-date text-primary font-weight-medium">
                  {{ report.humanCreatedAt }}
                </div>
                <clinical-author-line
                  :author="report.author"
                  class="mb-2"
                />
                <clinical-void-mark
                  :voided="report.voided"
                  :details="report.voidDetails"
                  class="mb-2"
                />
                <div
                  class="timeline-text"
                  :class="{ 'text-decoration-line-through text-medium-emphasis': report.voided }"
                >
                  <div
                    v-if="report.formattedText"
                    class="pre-wrap"
                  >
                    {{ report.formattedText }}
                  </div>
                  <div
                    v-else
                    class="text-body-1"
                  >
                    {{ report.text }}
                  </div>
                </div>
              </div>
            </div>
            <v-divider
              v-if="index < props.shift.reportTexts.length - 1"
              class="mt-4"
            />
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          @click="closeDialog"
        >
          {{ t('shiftReportTextsTimeline.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<style scoped>
.fixed-height {
  height: 600px;
}

.timeline-container {
  position: relative;
}

.timeline-item {
  position: relative;
}

.timeline-dot {
  position: relative;
  z-index: 1;
}

.timeline-item:not(:last-child) .timeline-dot::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 16px;
  width: 2px;
  height: 40px;
  background-color: rgb(var(--v-theme-primary), .3);
  transform: translateX(-50%);
}

.timeline-content {
  position: relative;
}

.timeline-date {
  font-size: .875rem;
}

.timeline-text {
  background-color: rgb(var(--v-theme-surface), .8);
  border-radius: 8px;
  padding: 12px;
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.pre-wrap {
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>
